import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../state/cartContext.jsx";
import { formatCurrency } from "@/lib/format";
import { Button } from "./ui";

const MAX_ITEMS_PREVIEW = 3;

export default function MiniCart() {
  const { cart, total } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const itemCount = cart.reduce((sum, item) => sum + item.qty, 0);

  if (cart.length === 0) return null;

  const previewItems = cart.slice(0, MAX_ITEMS_PREVIEW);
  const remainingCount = cart.length - MAX_ITEMS_PREVIEW;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Main button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`bg-neutral-900 text-white rounded-full p-4 shadow-2xl hover:bg-neutral-800 transition-all duration-200 ${
          isOpen ? "scale-95" : "scale-100"
        }`}
        aria-label="Visa varukorg"
      >
        <div className="relative">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          {itemCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-rose-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
              {itemCount > 9 ? "9+" : itemCount}
            </span>
          )}
        </div>
      </button>

      {/* Mini cart dropdown */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden">
          {/* Header */}
          <div className="bg-neutral-900 text-white px-4 py-3 flex items-center justify-between">
            <span className="font-semibold">Din varukorg</span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-neutral-400 hover:text-white"
              aria-label="Stäng"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Item preview */}
          <div className="max-h-64 overflow-y-auto">
            {previewItems.map((item) => (
              <div
                key={item._cartKey}
                className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100 last:border-0"
              >
                {item.image_url && (
                  <div className="h-12 w-12 rounded-lg overflow-hidden bg-neutral-100 flex-shrink-0">
                    <img src={item.image_url} alt="" className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">
                    {item.name}
                  </p>
                  {item.variant_value && (
                    <p className="text-xs text-neutral-500">
                      {item.variant_value}
                    </p>
                  )}
                  <p className="text-xs text-neutral-600">
                    {item.qty} × {formatCurrency(item.price_sek)}
                  </p>
                </div>
                <div className="text-sm font-semibold text-neutral-900 text-right">
                  {formatCurrency(item.price_sek * item.qty)}
                </div>
              </div>
            ))}
            {remainingCount > 0 && (
              <div className="px-4 py-2 bg-neutral-50 text-center text-sm text-neutral-600">
                +{remainingCount} till...
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-neutral-50 border-t border-neutral-200">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-neutral-600">Totalt</span>
              <span className="text-lg font-bold text-neutral-900">
                {formatCurrency(total)}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                as={Link}
                to="/shop"
                variant="outline"
                className="flex-1"
                onClick={() => setIsOpen(false)}
              >
                Fortsätt handla
              </Button>
              <Button
                as={Link}
                to="/checkout"
                className="flex-1"
                onClick={() => setIsOpen(false)}
              >
                Till kassan
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
